import {FC, useState, ChangeEvent} from 'react'
import {useNavigate} from 'react-router-dom'
import {api, ApiError} from '../../services/api'
import {Spinner} from '../../components/Spinner'
import {useToast} from '../../components/Toast'
import {PageHead} from './ui'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 13px',
  border: '1px solid #e5e7eb',
  borderRadius: 9,
  fontSize: 14,
  background: '#fff',
  boxSizing: 'border-box',
  outline: 'none',
}

const Label: FC<{children: React.ReactNode; required?: boolean}> = ({children, required}) => (
  <label style={{display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6}}>
    {children} {required && <span style={{color: '#d9214e'}}>*</span>}
  </label>
)

const FileField: FC<{label: string; required?: boolean; file: File | null; onPick: (f: File | null) => void; hint?: string}> = ({label, required, file, onPick, hint}) => {
  const onChange = (e: ChangeEvent<HTMLInputElement>) => onPick(e.target.files?.[0] ?? null)
  return (
    <div>
      <Label required={required}>{label}</Label>
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          border: `1px dashed ${file ? ACCENT : '#cbd2e0'}`,
          borderRadius: 10,
          padding: '14px 16px',
          cursor: 'pointer',
          background: file ? 'rgba(3,149,64,0.05)' : '#fafbfc',
        }}
      >
        <span style={{fontSize: 22}}>{file ? '✅' : '📷'}</span>
        <span style={{fontSize: 14, color: file ? INK : MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
          {file ? file.name : 'Choose an image or PDF…'}
        </span>
        <input type='file' accept='image/jpeg,image/png,image/webp,application/pdf' onChange={onChange} style={{display: 'none'}} />
      </label>
      {hint && <div style={{fontSize: 12, color: MUTED, marginTop: 5}}>{hint}</div>}
    </div>
  )
}

/** Renter onboarding application — flight-attendant verification + uploads. */
const ApplyPage: FC = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const [airline, setAirline] = useState('')
  const [employeeId, setEmployeeId] = useState('')
  const [badge, setBadge] = useState<File | null>(null)
  const [id, setId] = useState<File | null>(null)
  const [phone, setPhone] = useState('')
  const [ecName, setEcName] = useState('')
  const [ecPhone, setEcPhone] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setError(null)
    if (!badge) return setError('Please upload your airline badge.')
    if (!id) return setError('Please upload your government ID.')
    setSubmitting(true)
    try {
      const badgeUp = await api.uploadFile(badge)
      const idUp = await api.uploadFile(id)
      await api.submitApplication({
        airline,
        employeeId,
        phone,
        badgeKey: badgeUp.key,
        idKey: idUp.key,
        emergencyContactName: ecName,
        emergencyContactPhone: ecPhone,
      })
      toast.success('Application submitted — we\'ll review it shortly.')
      navigate('/home')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not submit your application')
      setSubmitting(false)
    }
  }

  const valid =
    airline.trim() && employeeId.trim() && phone.trim() && badge && id && ecName.trim() && ecPhone.trim()

  return (
    <div>
      <PageHead icon='🪪' eyebrow='Verification' title='Renter application' sub="Verify you're active flight crew so we can approve you for booking." />

      <div style={{background: '#fff', borderRadius: 16, padding: '28px 26px', boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>
        {error && (
          <div style={{background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 9, padding: '11px 14px', fontSize: 14, marginBottom: 20}}>{error}</div>
        )}

        <h2 style={{fontSize: 16, fontWeight: 800, color: INK, margin: '0 0 16px'}}>Airline verification</h2>
        <div className='row g-3 mb-4'>
          <div className='col-md-6'>
            <Label required>Airline</Label>
            <input style={inputStyle} value={airline} onChange={(e) => setAirline(e.target.value)} placeholder='e.g. Delta Air Lines' />
          </div>
          <div className='col-md-6'>
            <Label required>Employee / badge ID</Label>
            <input style={inputStyle} value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} placeholder='e.g. DL123456' />
          </div>
        </div>

        <div className='row g-3 mb-4'>
          <div className='col-md-6'>
            <FileField label='Airline badge photo' required file={badge} onPick={setBadge} hint='Clear photo of your crew/airline ID badge.' />
          </div>
          <div className='col-md-6'>
            <FileField label='Government ID' required file={id} onPick={setId} hint="Driver's license or passport — used to verify your identity." />
          </div>
        </div>

        <h2 style={{fontSize: 16, fontWeight: 800, color: INK, margin: '24px 0 16px'}}>Your phone number</h2>
        <div className='row g-3 mb-2'>
          <div className='col-md-6'>
            <Label required>Mobile number</Label>
            <input style={inputStyle} type='tel' value={phone} onChange={(e) => setPhone(e.target.value)} placeholder='e.g. (415) 555-0123' />
            <div style={{fontSize: 12.5, color: MUTED, marginTop: 6}}>So we can reach you about your stay — arrival, the door, anything urgent.</div>
          </div>
        </div>

        <h2 style={{fontSize: 16, fontWeight: 800, color: INK, margin: '24px 0 16px'}}>Emergency contact</h2>
        <div className='row g-3 mb-2'>
          <div className='col-md-6'>
            <Label required>Contact name</Label>
            <input style={inputStyle} value={ecName} onChange={(e) => setEcName(e.target.value)} />
          </div>
          <div className='col-md-6'>
            <Label required>Contact phone</Label>
            <input style={inputStyle} value={ecPhone} onChange={(e) => setEcPhone(e.target.value)} />
          </div>
        </div>

        <div style={{display: 'flex', gap: 12, marginTop: 24}}>
          <button
            onClick={submit}
            disabled={submitting || !valid}
            style={{fontWeight: 700, fontSize: 15, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '12px 28px', cursor: submitting || !valid ? 'not-allowed' : 'pointer', opacity: submitting || !valid ? 0.6 : 1}}
          >
            {submitting ? <Spinner size={14} /> : 'Submit application'}
          </button>
          <button onClick={() => navigate('/home')} style={{fontWeight: 600, fontSize: 15, color: INK, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 24px', cursor: 'pointer'}}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

export {ApplyPage}
